import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ArrowLeft, AlertTriangle, CheckCircle2, Flag, FileEdit } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuthStore } from '@/store/authStore';
import { getDSRDetail, updateDSRReviewStatus, type DSRDetailData } from '@/services/db/dsrDetail';
import { canAmend, amendDeadline } from '@/utils/dsr';
import AnnotationPanel from '@/components/dsr/AnnotationPanel';
import RiskScoreCard from '@/components/dsr/RiskScoreCard';

import DailySetupSection from '@/components/dsr/DailySetupSection';
import WorkforceSection from '@/components/dsr/WorkforceSection';
import ActivitiesSection from '@/components/dsr/ActivitiesSection';
import EquipmentSection from '@/components/dsr/EquipmentSection';
import HSEObservationsSection from '@/components/dsr/HSEObservationsSection';
import PermitsSection from '@/components/dsr/PermitsSection';
import ShiftBriefCard from '@/components/dsr/ShiftBriefCard';
import SafetyMeetingsSection from '@/components/dsr/SafetyMeetingsSection';
import JSAEvaluationsSection from '@/components/dsr/JSAEvaluationsSection';
import SWAEventsSection from '@/components/dsr/SWAEventsSection';
import SafetyEventsSection from '@/components/dsr/SafetyEventsSection';
import ForecastingSection from '@/components/dsr/ForecastingSection';
import NarrativeSection from '@/components/dsr/NarrativeSection';
import ShiftLogSection from '@/components/dsr/ShiftLogSection';
import ActionItemsSection from '@/components/dsr/ActionItemsSection';

// ── Section definitions ──────────────────────────────────────

interface SectionDef {
  id: string;
  label: string;
  hasData: (d: DSRDetailData) => boolean;
}

const SECTIONS: SectionDef[] = [
  { id: 'section-daily-setup', label: 'Daily Setup', hasData: (d) => !!d.dailySetup },
  { id: 'section-workforce', label: 'Workforce', hasData: (d) => d.personnel.length > 0 },
  { id: 'section-activities', label: 'Activities', hasData: (d) => d.activities.length > 0 },
  { id: 'section-equipment', label: 'Equipment', hasData: (d) => d.equipment.length > 0 },
  { id: 'section-hse-observations', label: 'HSE Observations', hasData: (d) => d.hseObservations.length > 0 },
  { id: 'section-permits', label: 'Permits', hasData: (d) => d.permits.length > 0 },
  { id: 'section-shift-brief', label: 'Shift Brief', hasData: () => true },
  { id: 'section-safety-meetings', label: 'Safety Meetings', hasData: (d) => d.meetings.length > 0 },
  { id: 'section-jsa-evaluations', label: 'JSA Evaluations', hasData: (d) => d.jsaEvaluations.length > 0 },
  { id: 'section-swa-events', label: 'SWA Events', hasData: (d) => d.swaEvents.length > 0 },
  { id: 'section-safety-events', label: 'Safety Events', hasData: (d) => d.safetyEvents.length > 0 },
  {
    id: 'section-forecasting',
    label: 'Forecasting',
    hasData: (d) => d.forecastedActivities.length > 0 || d.outstandingItems.length > 0 || !!d.forecastingSummary,
  },
  { id: 'section-shift-log', label: 'Shift Log', hasData: (d) => d.shiftLogs.length > 0 },
  { id: 'section-narrative', label: 'Shift Narrative (legacy)', hasData: (d) => !!d.narrative?.content },
  { id: 'section-action-items', label: 'Action Items', hasData: (d) => d.actionItems.length > 0 },
  { id: 'section-annotations', label: 'Annotations', hasData: (d) => d.annotations.length > 0 || d.report.status !== 'draft' },
];

// ── Status / Risk badges ─────────────────────────────────────

const STATUS_BADGE: Record<string, { className: string; label: string }> = {
  draft: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Draft' },
  submitted: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Submitted' },
  reviewed: { className: 'bg-mozaic-teal/15 text-mozaic-teal', label: 'Reviewed' },
  flagged: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Flagged' },
  amended: { className: 'bg-mozaic-blue/15 text-mozaic-blue', label: 'Amended' },
};

const REVIEW_ROLES = ['supervisor', 'safety_manager', 'tenant_admin', 'platform_admin'];

const RISK_BADGE: Record<string, { className: string; label: string }> = {
  low: { className: 'bg-mozaic-green/15 text-mozaic-green', label: 'Low' },
  medium: { className: 'bg-mozaic-orange/15 text-mozaic-orange', label: 'Medium' },
  high: { className: 'bg-mozaic-red/15 text-mozaic-red', label: 'High' },
  critical: { className: 'bg-mozaic-red/20 text-mozaic-red font-bold', label: 'Critical' },
};

// ── Component ────────────────────────────────────────────────

export default function DSRDetailPage() {
  const { dsrId } = useParams<{ dsrId: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userRole = useAuthStore((s) => s.userRole);

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['dsr_detail', dsrId],
    queryFn: () => getDSRDetail(dsrId!),
    enabled: !!dsrId,
    staleTime: 5 * 60 * 1000,
  });

  // ── Review status mutation ────────────────────────────────
  const [flagMode, setFlagMode] = useState(false);

  const reviewMutation = useMutation({
    mutationFn: (status: 'reviewed' | 'flagged') =>
      updateDSRReviewStatus(dsrId!, status, user!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dsr_detail', dsrId] });
    },
  });

  // ── IntersectionObserver for active section tracking ───────
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (!data) return;

    observerRef.current?.disconnect();
    const entries = new Map<string, IntersectionObserverEntry>();

    observerRef.current = new IntersectionObserver(
      (observed) => {
        observed.forEach((entry) => entries.set(entry.target.id, entry));
        // Find topmost visible section
        for (const section of SECTIONS) {
          const e = entries.get(section.id);
          if (e?.isIntersecting) {
            setActiveSection(section.id);
            break;
          }
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: 0 }
    );

    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) observerRef.current!.observe(el);
    });

    return () => observerRef.current?.disconnect();
  }, [data]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Loading state ──────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-6 w-64" />
        <Skeleton className="h-4 w-48" />
        <div className="grid gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────
  if (isError || !data) {
    return (
      <div className="flex flex-col items-center gap-4 py-20">
        <p className="text-sm text-muted-foreground">Could not load this DSR report.</p>
        <Button variant="outline" onClick={() => navigate('/reports')}>
          Back to Reports
        </Button>
      </div>
    );
  }

  const { report } = data;
  const project = report.projects;
  const submitter = report.users;
  const summaries = report.dsr_activities_summaries;
  // Prefer the DSR-level composite risk (computed or SSR-overridden).
  // Fall back to the activities-summary risk for legacy DSRs that
  // predate the composite field.
  const riskLevel = report.overall_risk_level ?? summaries?.[0]?.overall_risk_level ?? null;
  const statusBadge = STATUS_BADGE[report.status] ?? STATUS_BADGE.draft;
  const riskBadge = riskLevel ? RISK_BADGE[riskLevel] : null;
  // Supervisor+ and the creating SSR can manually recompute.
  const canRecompute =
    (!!userRole && ['supervisor', 'safety_manager', 'tenant_admin', 'platform_admin'].includes(userRole)) ||
    (userRole === 'ssr' && report.created_by === user?.id);

  const showAmend = canAmend(report, user?.id ?? '', userRole ?? '');
  const showDraftEdit = report.status === 'draft' && report.created_by === user?.id;
  const deadline = report.submitted_at ? amendDeadline(report.submitted_at) : null;
  const canReview = !!userRole && REVIEW_ROLES.includes(userRole) && report.status === 'submitted';
  const tenantId = useAuthStore.getState().tenantId ?? report.tenant_id;

  // Sections with data for the nav
  const navSections = SECTIONS.filter((s) => s.hasData(data));

  return (
    <div className="space-y-4">
      {/* Back link */}
      <button
        onClick={() => navigate('/reports')}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Reports
      </button>

      {/* Page header */}
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <h1 className="text-lg font-semibold">
            {project?.name ?? 'Unknown Project'} — {format(new Date(report.report_date), 'MMM d, yyyy')}
          </h1>
          <Badge className="capitalize">{report.shift_type} Shift</Badge>
          <Badge className={statusBadge.className}>{statusBadge.label}</Badge>
          {riskBadge && <Badge className={riskBadge.className}>{riskBadge.label} Risk</Badge>}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {submitter && (
            <span>
              Submitted by {submitter.first_name} {submitter.last_name}
            </span>
          )}
          {report.submitted_at && (
            <span>on {format(new Date(report.submitted_at), "MMM d, yyyy 'at' HH:mm")}</span>
          )}
        </div>
        {report.status === 'submitted' && !showAmend && !canReview && (
          <p className="text-xs text-muted-foreground">Submitted — read only</p>
        )}
      </div>

      {/* Composite risk score card — ARCH-030 */}
      <RiskScoreCard report={report} canRecompute={canRecompute} />

      {/* Review action buttons — supervisor+ on submitted DSRs */}
      {canReview && (
        <div className="flex flex-wrap gap-2">
          <Button
            size="sm"
            className="bg-mozaic-teal hover:bg-mozaic-teal/90"
            disabled={reviewMutation.isPending}
            onClick={() => reviewMutation.mutate('reviewed')}
          >
            <CheckCircle2 className="mr-1.5 h-4 w-4" />
            Mark as Reviewed
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="border-mozaic-orange text-mozaic-orange hover:bg-mozaic-orange/10"
            disabled={reviewMutation.isPending}
            onClick={() => {
              setFlagMode(true);
              document.getElementById('section-annotations')?.scrollIntoView({ behavior: 'smooth' });
            }}
          >
            <Flag className="mr-1.5 h-4 w-4" />
            Flag for Follow-up
          </Button>
          {reviewMutation.isError && (
            <span className="text-xs text-destructive self-center">Update failed. Try again.</span>
          )}
        </div>
      )}

      {/* Draft edit banner — creator can continue editing with no time limit */}
      {showDraftEdit && (
        <Card className="border-mozaic-orange/40 bg-mozaic-orange/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="flex items-center gap-2">
              <FileEdit className="h-4 w-4 text-mozaic-orange" />
              <span className="text-sm">This report is still a draft. Continue editing to submit it.</span>
            </div>
            <Link
              to={`/dsr/${dsrId}/edit`}
              className="inline-flex h-7 items-center rounded-md border border-mozaic-orange px-2.5 text-sm font-medium text-mozaic-orange hover:bg-mozaic-orange/10"
            >
              Continue Editing
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Amendment banner */}
      {showAmend && deadline && (
        <Card className="border-mozaic-orange/40 bg-mozaic-orange/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-mozaic-orange" />
              <span className="text-sm">
                This report can be amended until{' '}
                <strong>{format(deadline, "MMM d, yyyy 'at' HH:mm")}</strong>.
                Amendments are tracked.
              </span>
            </div>
            <Link
              to={`/reports/${dsrId}/amend`}
              className="inline-flex h-7 items-center rounded-md border border-mozaic-orange px-2.5 text-sm font-medium text-mozaic-orange hover:bg-mozaic-orange/10"
            >
              Amend Report
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Amendment info bar — shown when status = 'amended' */}
      {report.status === 'amended' && (() => {
        const amendAnnotation = data.annotations
          .filter((a) => a.annotation_type === 'amendment')
          .at(-1);
        const amenderName = amendAnnotation?.users
          ? `${amendAnnotation.users.first_name} ${amendAnnotation.users.last_name}`
          : 'Unknown';
        return (
          <Card className="border-mozaic-blue/40 bg-mozaic-blue/5">
            <CardContent className="py-3">
              <div className="flex items-start gap-2">
                <FileEdit className="mt-0.5 h-4 w-4 shrink-0 text-mozaic-blue" />
                <div className="space-y-0.5">
                  <p className="text-sm font-medium text-mozaic-blue">
                    Amended by {amenderName}
                    {report.amended_at &&
                      ` on ${format(new Date(report.amended_at), "MMM d, yyyy 'at' HH:mm")}`}
                  </p>
                  {amendAnnotation && (
                    <p className="text-sm text-muted-foreground">
                      Reason: {amendAnnotation.body}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Late entry reason */}
      {report.late_entry_reason && (
        <Card className="border-mozaic-orange/40 bg-mozaic-orange/5">
          <CardContent className="py-3">
            <p className="text-sm">
              <span className="font-medium">Late entry reason:</span> {report.late_entry_reason}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Main content: section nav + sections */}
      <div className="flex gap-6">
        {/* Desktop section nav */}
        <nav className="hidden w-52 shrink-0 md:block">
          <div className="sticky top-20 space-y-0.5">
            <p className="mb-2 text-xs font-medium uppercase text-muted-foreground">Sections</p>
            {navSections.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                className={`block w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors ${
                  activeSection === s.id
                    ? 'bg-mozaic-teal/10 font-medium text-mozaic-teal'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Mobile section dropdown */}
        <div className="sticky top-14 z-10 -mx-4 mb-2 block bg-background px-4 py-2 md:hidden">
          <Select value={activeSection} onValueChange={(v) => v && scrollTo(v)}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Jump to section..." />
            </SelectTrigger>
            <SelectContent>
              {navSections.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Section content */}
        <div className="min-w-0 flex-1 space-y-8">
          <DailySetupSection data={data.dailySetup} />
          <WorkforceSection data={data.personnel} />
          <ActivitiesSection data={data.activities} />
          <EquipmentSection data={data.equipment} />
          <HSEObservationsSection data={data.hseObservations} photos={data.photoAttachments} />
          <PermitsSection data={data.permits} />
          <section id="section-shift-brief">
            <ShiftBriefCard
              dsrId={report.id}
              tenantId={report.tenant_id}
              reportStatus={report.status}
              mode="detail"
            />
          </section>
          <SafetyMeetingsSection data={data.meetings} />
          <JSAEvaluationsSection data={data.jsaEvaluations} photos={data.photoAttachments} />
          <SWAEventsSection data={data.swaEvents} photos={data.photoAttachments} />
          <SafetyEventsSection data={data.safetyEvents} photos={data.photoAttachments} />
          <ForecastingSection
            forecastedActivities={data.forecastedActivities}
            outstandingItems={data.outstandingItems}
            summary={data.forecastingSummary}
          />
          <ShiftLogSection data={data.shiftLogs} />
          <NarrativeSection data={data.narrative} />
          <ActionItemsSection data={data.actionItems} />

          {/* Annotations panel — visible for all roles, editable for supervisor+ */}
          {report.status !== 'draft' && (
            <AnnotationPanel
              dsrId={report.id}
              tenantId={tenantId}
              annotations={data.annotations}
              defaultType={flagMode ? 'flag' : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
}
